import type {
  AIUsageAccountType,
  AIUsageAmount,
  AIUsageColumn,
} from '@kizen/api/billing';
import { NO_VALUE } from 'components/Kizen/Table';
import { formatBillingCurrency } from '../util';

export type MappedUsageColumn = {
  column: AIUsageColumn;
  amount?: AIUsageAmount;
  formatted: string;
};

const NANO_DOLLARS_PER_DOLLAR = 1_000_000_000;

// Half a cent in nano-dollars — the accounting-standard rounding boundary
// below which a nonzero USD amount is shown as "<$0.01" instead of "$0.00",
// since to a user that reads identically to no usage at all.
const SUB_CENT_NANO_DOLLARS = 5_000_000;
const SUB_CENT_LABEL = '< $0.01';

// cost_unit: 'k' is a raw Kizen credit count; 'u' (user's own API key) and
// 'p' (Kizen passthrough billing) are both USD nano-dollars, formatted
// identically. Per AC, a zero amount and a column that doesn't apply to this
// row both render as "—" — checked as an exact 0 so it can't also swallow NaN.
export const formatAIUsageAmount = (
  costAmount: number,
  costUnit: AIUsageColumn['cost_unit']
): string => {
  if (costAmount === 0) {
    return NO_VALUE;
  }

  if (costUnit === 'k') {
    return costAmount.toLocaleString();
  }

  if (costAmount > 0 && costAmount < SUB_CENT_NANO_DOLLARS) {
    return SUB_CENT_LABEL;
  }

  return formatBillingCurrency(costAmount / NANO_DOLLARS_PER_DOLLAR);
};

export const mapUsageToColumns = (
  usage: AIUsageAmount[] | undefined,
  columns: AIUsageColumn[]
): MappedUsageColumn[] => {
  const byKey = new Map((usage ?? []).map((entry) => [entry.key, entry]));

  return columns.map((column) => {
    const amount = byKey.get(column.key);
    return {
      column,
      amount,
      formatted: formatAIUsageAmount(
        amount?.cost_amount ?? 0,
        column.cost_unit
      ),
    };
  });
};

// Kizen AI credits column is always shown; provider columns are hidden if
// they have zero usage across every row passed in.
export const getVisibleColumns = (
  columns: AIUsageColumn[],
  allRowsUsage: AIUsageAmount[][]
): AIUsageColumn[] =>
  columns.filter((column) => {
    if (column.cost_unit === 'k') {
      return true;
    }

    return allRowsUsage.some((usage) =>
      usage.some((entry) => entry.key === column.key && entry.cost_amount !== 0)
    );
  });

export const getKizenCreditsOnlyColumns = (
  columns: AIUsageColumn[]
): AIUsageColumn[] => columns.filter((column) => column.cost_unit === 'k');

export const isServiceAccount = (
  accountType: AIUsageAccountType | null
): boolean => accountType !== null && accountType !== 'user_standard';
